/**
 * PriorityDropdown Component
 * 
 * Componente de dropdown para seleção de prioridade.
 * Baseado no padrão do DropdownMenu de NotasFiscais.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Circle } from 'lucide-react';
import './PriorityDropdown.css';

interface PriorityDropdownProps {
  value: 'normal' | 'urgent' | 'critical';
  onChange: (value: 'normal' | 'urgent' | 'critical') => void;
  buttonRef?: React.RefObject<HTMLButtonElement>;
}

const priorityOptions = [
  { value: 'normal' as const, label: 'Normal', color: '#6b7280' },
  { value: 'urgent' as const, label: 'Urgente', color: '#f59e0b' },
  { value: 'critical' as const, label: 'Crítico (Faltando)', color: '#dc2626' }
];

function PriorityDropdown({ value, onChange, buttonRef }: PriorityDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = priorityOptions.find(opt => opt.value === value) || priorityOptions[0];

  useEffect(() => {
    const updatePosition = () => {
      if (menuRef.current && (triggerRef.current || buttonRef?.current)) {
        const ref = triggerRef.current || buttonRef?.current;
        if (ref) {
          const rect = ref.getBoundingClientRect();
          menuRef.current.style.top = `${rect.bottom + 8}px`;
          menuRef.current.style.left = `${rect.left}px`;
          menuRef.current.style.width = `${rect.width}px`;
        }
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, buttonRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        (!buttonRef?.current || !buttonRef.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, buttonRef]);

  const handleSelect = (priority: 'normal' | 'urgent' | 'critical') => {
    onChange(priority);
    setIsOpen(false);
  };

  return (
    <div className="priority-dropdown-wrapper">
      <button
        ref={triggerRef}
        type="button"
        className="priority-dropdown-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="priority-dropdown-selected">
          <Circle size={12} fill={selectedOption.color} color={selectedOption.color} />
          <span>{selectedOption.label}</span>
        </div>
        <ChevronDown size={16} className={isOpen ? 'open' : ''} />
      </button>
      {isOpen && (
        <div ref={menuRef} className="priority-dropdown-menu">
          {priorityOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`priority-dropdown-option ${value === option.value ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              <Circle size={12} fill={option.color} color={option.color} />
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default PriorityDropdown;
